import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router'

import NotFound from 'secondstep-components/src/components/NotFound'
import Unauthorized from 'secondstep-components/src/components/Unauthorized'

import LicenseManagement from 'pages/LicenseManagement'
import UserManagement from 'pages/UserManagement'
import TrainingReport from 'pages/TrainingReport'
import { CORE_LESSON_COMPLETION_FLAG } from 'pages/TrainingReport/constants'
import isFlagOn from 'utils/featureFlags'
import { paths } from './paths'
import { getLUMDefaultPath } from './helpers'

const PrivateRoute = ({
  component: Component,
  path,
  authed,
  redirectTo,
  exact: isExact = true,
  ...rest
}) => (
  <Route
    exact={isExact}
    path={path}
    render={props =>
      authed ? (
        <Component {...props} {...rest} />
      ) : redirectTo ? (
        <Redirect to={{ pathname: redirectTo }} />
      ) : (
        <Unauthorized location={props.location} />
      )
    }
  />
)

PrivateRoute.propTypes = {
  authed: PropTypes.bool.isRequired,
  component: PropTypes.elementType.isRequired,
  exact: PropTypes.bool,
  path: PropTypes.string.isRequired,
  redirectTo: PropTypes.string,
}

const LUMRouter = ({ pushTo, isLicenseAdmin, isSiteAdmin, sitesData }) => {
  const useNewTrainingReportUrls = isFlagOn([CORE_LESSON_COMPLETION_FLAG])
  const defaultPath = useMemo(
    () =>
      getLUMDefaultPath({
        isLicenseAdmin,
        isSiteAdmin,
        sites: sitesData,
      }),
    [isLicenseAdmin, isSiteAdmin, sitesData],
  )

  return (
    <Switch>
      <PrivateRoute
        authed={isLicenseAdmin}
        component={LicenseManagement}
        path={paths.UNASSIGNED_LICENSES}
        pushTo={pushTo}
        redirectTo={defaultPath}
      />
      <PrivateRoute
        authed={isLicenseAdmin}
        component={LicenseManagement}
        path={paths.ASSIGNED_LICENSES}
        pushTo={pushTo}
        redirectTo={defaultPath}
      />
      <PrivateRoute
        authed={isLicenseAdmin}
        component={LicenseManagement}
        path={paths.ASSIGN_LICENSE}
        pushTo={pushTo}
        redirectTo={defaultPath}
      />
      <PrivateRoute
        authed={isLicenseAdmin}
        component={LicenseManagement}
        path={paths.INACTIVE_LICENSES}
        pushTo={pushTo}
        redirectTo={defaultPath}
      />
      <PrivateRoute
        authed={isSiteAdmin}
        component={UserManagement}
        exact={false}
        path={paths.USER_MANAGEMENT}
        pushTo={pushTo}
        redirectTo={defaultPath}
        userInvite={false}
      />
      {useNewTrainingReportUrls ? (
        <PrivateRoute
          authed={isSiteAdmin}
          component={TrainingReport}
          exact={false}
          path={paths.TRAINING_REPORT_SLUG_SITEID}
          pushTo={pushTo}
          redirectTo={defaultPath}
          userInvite={false}
        />
      ) : null}
      {useNewTrainingReportUrls ? (
        <PrivateRoute
          authed={isSiteAdmin}
          component={TrainingReport}
          exact={false}
          path={paths.TRAINING_REPORT_SLUG}
          pushTo={pushTo}
          redirectTo={defaultPath}
          userInvite={false}
        />
      ) : null}
      {!useNewTrainingReportUrls ? (
        <PrivateRoute
          authed={isSiteAdmin}
          component={TrainingReport}
          exact={false}
          path={paths.TRAINING_REPORT_SITE}
          pushTo={pushTo}
          redirectTo={defaultPath}
          userInvite={false}
        />
      ) : null}
      {defaultPath ? (
        <Redirect exact from="/" to={defaultPath} />
      ) : (
        <Route
          exact
          path="/"
          render={({ location }) => <Unauthorized location={location} />}
        />
      )}
      {defaultPath ? (
        <Redirect exact from={paths.BASE_LICENSE_MANAGEMENT} to={defaultPath} />
      ) : (
        <Route
          exact
          path={paths.BASE_LICENSE_MANAGEMENT}
          render={({ location }) => <Unauthorized location={location} />}
        />
      )}
      <Route component={NotFound} />
    </Switch>
  )
}

LUMRouter.propTypes = {
  isLicenseAdmin: PropTypes.bool.isRequired,
  isSiteAdmin: PropTypes.bool.isRequired,
  pushTo: PropTypes.func,
  sitesData: PropTypes.array,
}

export default LUMRouter
